import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Crown, Loader2, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const pollStatus = async () => {
      if (!sessionId || !token) {
        setStatus('error');
        return;
      }

      try {
        const response = await axios.get(`${API}/subscription/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          // Refresh user data to get updated subscription status
          await refreshUser();
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('timeout');
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollStatus();
  }, [sessionId, token, attempts, refreshUser]);

  return (
    <div className="pt-24 pb-16 min-h-screen flex items-center justify-center" data-testid="subscription-success-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full mx-6 text-center"
      >
        {status === 'checking' && (
          <div className="bg-zinc-900/50 border border-white/10 rounded-2xl p-12">
            <Loader2 className="w-16 h-16 text-yellow-400 animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-2">Activating Subscription</h1>
            <p className="text-zinc-400">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-zinc-900/50 border border-yellow-500/30 rounded-2xl p-12">
            <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
              <Crown className="w-10 h-10 text-yellow-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Welcome to Premium!</h1>
            <p className="text-zinc-400 mb-4">
              Your subscription is now active. You'll enjoy only 4% commission on all job payments.
            </p>
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-8">
              <div className="flex items-center justify-center gap-2 text-green-400">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">Subscription Active</span>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-none"
                data-testid="go-to-dashboard"
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/agents')}
                className="border-white/20 text-zinc-300"
              >
                Browse Agents
              </Button>
            </div>
          </div>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <div className="bg-zinc-900/50 border border-red-500/30 rounded-2xl p-12">
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">⚠️</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
            </h1>
            <p className="text-zinc-400 mb-8">
              We couldn't confirm your subscription. Please try again or contact support.
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/subscription')}
                className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-none"
              >
                Try Again
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="border-white/20 text-zinc-300"
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
